import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { DEFAULT_XP_GOAL } from '../config/constants';
import { PublicProfileService } from './PublicProfileService';

const TODAY_KEY = 'xp_today';
const DATE_KEY  = 'xp_today_date';
const TOTAL_KEY = 'xp_total';
const GOAL_KEY  = 'xp_goal';
const WEEK_KEY       = 'xp_week';
const WEEK_START_KEY = 'xp_week_start';
export const DEFAULT_GOAL = DEFAULT_XP_GOAL;

// Local calendar date (not UTC) so "today" agrees with StreakService, which
// also uses local date components — otherwise today's XP and the streak
// roll over at different times for anyone not in UTC.
function todayLocalDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Monday of the current local week, as the leaderboard's rolling window.
function weekStartLocalDate() {
  const d = new Date();
  const day = d.getDay(); // 0=Sun..6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function maybeReset() {
  const today = todayLocalDate();
  if (localStorage.getItem(DATE_KEY) !== today) {
    localStorage.setItem(TODAY_KEY, '0');
    localStorage.setItem(DATE_KEY, today);
  }
}

function maybeResetWeekly() {
  const weekStart = weekStartLocalDate();
  if (localStorage.getItem(WEEK_START_KEY) !== weekStart) {
    localStorage.setItem(WEEK_KEY, '0');
    localStorage.setItem(WEEK_START_KEY, weekStart);
  }
}

export const XpService = {
  getTodayXp()  { maybeReset(); return parseInt(localStorage.getItem(TODAY_KEY) || '0', 10); },
  getTotalXp()  { return parseInt(localStorage.getItem(TOTAL_KEY) || '0', 10); },
  getWeeklyXp() { maybeResetWeekly(); return parseInt(localStorage.getItem(WEEK_KEY) || '0', 10); },
  getGoal()     { return parseInt(localStorage.getItem(GOAL_KEY)  || String(DEFAULT_GOAL), 10); },
  setGoal(g)    { localStorage.setItem(GOAL_KEY, String(g)); },

  async addXp(amount) {
    maybeReset();
    maybeResetWeekly();
    const goal     = this.getGoal();
    const prevToday = this.getTodayXp();
    const prevTotal = this.getTotalXp();
    const prevWeekly = this.getWeeklyXp();
    const newToday  = prevToday + amount;
    const newTotal  = prevTotal + amount;
    const newWeekly = prevWeekly + amount;
    const goalJustReached = prevToday < goal && newToday >= goal;

    localStorage.setItem(TODAY_KEY, String(newToday));
    localStorage.setItem(TOTAL_KEY, String(newTotal));
    localStorage.setItem(WEEK_KEY, String(newWeekly));

    const uid = auth?.currentUser?.uid;
    if (uid && db) {
      setDoc(doc(db, 'users', uid), { xpTotal: newTotal }, { merge: true })
        .catch((e) => console.warn('XpService: Firestore sync failed', e));
      PublicProfileService.sync({ weeklyXp: newWeekly }).catch(() => {});
    }
    return { todayXp: newToday, totalXp: newTotal, weeklyXp: newWeekly, goal, goalJustReached };
  },

  /**
   * Reconciles the local total with Firestore on login so a fresh device or
   * cleared localStorage doesn't silently overwrite the cloud total with a
   * lower value on the next addXp() write. Takes the max of the two totals
   * since either side may hold more recent progress.
   */
  hydrateFromRemote(remoteData) {
    const remoteTotal = Number(remoteData?.xpTotal) || 0;
    const localTotal = this.getTotalXp();
    if (remoteTotal > localTotal) {
      localStorage.setItem(TOTAL_KEY, String(remoteTotal));
    }
  },
};
